import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "react-i18next";
import {
  DoraButton,
  DoraCheckbox,
  DoraTextInput,
  UFDoraTextInput,
} from "dora-shared/frontend";
import { useAppDispatch, useSelector } from "../../../../../redux-store";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  addBusinessCentralClient,
  getBusinessCentralClientForEdit,
  updateBusinessCentralClient,
} from "../../../../../ducks/data/clients";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { selectClients } from "../../../../../ducks/data/clients/selectors";
import { notifyL } from "../../../../../ducks/notifications";
import axios from "../../../../../axios";

const AddEditBusinessCentralCustomerDialog = ({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation(["settings", "components"]);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [searchRequestInProgress, setSearchRequestInProgress] = useState(false);
  const [loadingEditData, setLoadingEditData] = useState(!!id);
  const [searchValue, setSearchValue] = useState("");
  const dispatch = useAppDispatch();

  const clients = useSelector(selectClients);

  const formMethods = useForm({
    defaultValues: {
      name: "",
      organizationNumber: "",
      address: "",
      postcode: "",
      city: "",
      email: "",
      phoneNumberMobile: "",
      visibleToDrivers: true,
    },
    mode: "all",
    resolver: yupResolver(
      yup.object().shape({
        name: yup.string().required(),
        organizationNumber: yup.string().optional(),
        address: yup.string().optional(),
        postcode: yup.string().optional(),
        city: yup.string().optional(),
        email: yup
          .string()
          .email(
            t("settings:customersConfiguration.addCustomerDialog.errorEmail")
          )
          .matches(
            /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            t("settings:customersConfiguration.addCustomerDialog.errorEmail")
          )
          .optional(),
        phoneNumberMobile: yup.string().optional(),
        visibleToDrivers: yup.boolean().required(),
      }),
      { abortEarly: false }
    ),
  });

  const {
    register,
    getValues,
    formState,
    watch,
    handleSubmit,
    setValue,
    trigger,
  } = formMethods;

  useEffect(() => {
    if (id) {
      dispatch(getBusinessCentralClientForEdit(id))
        .unwrap()
        .then((data) => {
          setValue("name", data.name ?? "");
          setValue("organizationNumber", data.organizationNumber ?? "");
          setValue("address", data.address ?? "");
          setValue("postcode", data.postcode ?? "");
          setValue("city", data.city ?? "");
          setValue("email", data.email ?? "");
          setValue("phoneNumberMobile", data.phoneNumberMobile ?? "");
          void trigger();
        })
        .catch(() => {
          // The getBusinessCentralClientForEdit thunk has already reported it and shown the error toast.
        })
        .finally(() => setLoadingEditData(false));
    }
  }, [id, dispatch, setValue, trigger]);

  const nameWatch = watch("name");

  const nameExists = useMemo(
    () => clients.find((c) => c.client === nameWatch && c.id !== id),
    [clients, nameWatch, id]
  );

  const { isValid, errors } = formState;

  const onCreateClick = () => {
    handleSubmit(
      () => {
        setRequestInProgress(true);
        dispatch(addBusinessCentralClient(getValues()))
          .unwrap()
          .then(() => onClose())
          .catch(() => {
            // The addBusinessCentralClient thunk has already reported it and shown the error toast.
          })
          .finally(() => setRequestInProgress(false));
      },
      (err) => {
        console.error(err);
        setRequestInProgress(false);
      }
    )();
  };

  const onUpdateClick = () => {
    handleSubmit(
      () => {
        setRequestInProgress(true);
        const values = getValues();
        dispatch(
          updateBusinessCentralClient({
            clientId: id!,
            name: values.name,
            organizationNumber: values.organizationNumber,
            address: values.address,
            postcode: values.postcode,
            city: values.city,
            email: values.email,
            phoneNumberMobile: values.phoneNumberMobile,
          })
        )
          .unwrap()
          .then(() => onClose())
          .catch(() => {
            // The updateBusinessCentralClient thunk has already reported it and shown the error toast.
          })
          .finally(() => setRequestInProgress(false));
      },
      (err) => {
        console.error(err);
        setRequestInProgress(false);
      }
    )();
  };

  const onSearchClick = (e: any) => {
    e.preventDefault();
    setSearchRequestInProgress(true);
    const errNotif = notifyL({
      type: "error",
      key: "actionFailed",
      namespace: "notifications",
    });
    try {
      axios
        .get(`https://cvrapi.dk/api?search=${searchValue}&country=dk`)
        .then((res) => {
          if (res.data) {
            const { name, vat, address, zipcode, city, email, phone } =
              res.data;
            setSearchValue("");
            setValue("name", name || "");
            setValue("organizationNumber", vat ? String(vat) : "");
            setValue("address", address || "");
            setValue("postcode", zipcode ? String(zipcode) : "");
            setValue("city", city || "");
            setValue("email", email || "");
            setValue("phoneNumberMobile", phone ? String(phone) : "");
            void trigger();
          }
        })
        .catch((err) => {
          dispatch(errNotif);
          console.error(err);
        })
        .finally(() => setSearchRequestInProgress(false));
    } catch (err) {
      console.error(err);
      dispatch(errNotif);
      setSearchRequestInProgress(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth={"lg"}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isValid && !requestInProgress) {
            if (id) {
              onUpdateClick();
            } else {
              onCreateClick();
            }
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h2">
            {id
              ? t("settings:customersConfiguration.addCustomerDialog.titleEdit")
              : t("settings:customersConfiguration.addCustomerDialog.title")}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {loadingEditData ? (
            <div className="flex items-center justify-center py-12 px-24">
              <CircularProgress size={32} />
            </div>
          ) : (
            <>
              {!id && (
                <div className="mt-2 mb-6 flex gap-2 items-center w-full">
                  <DoraTextInput
                    style={{ minWidth: 430 }}
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.searchPlaceholder"
                    )}
                    value={searchValue}
                    onChange={setSearchValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (searchValue && !searchRequestInProgress) {
                          onSearchClick(e);
                        }
                      }
                    }}
                  />
                  <DoraButton
                    variant="primary-filled"
                    type="submit"
                    disabled={!searchValue || searchRequestInProgress}
                    onClick={onSearchClick}
                  >
                    {t("components:buttonLabels.search")}
                  </DoraButton>
                </div>
              )}
              <div className="grid gap-3 grid-cols-3">
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.name"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.name"
                    )}
                    register={register("name")}
                  />
                  {nameExists && (
                    <div
                      className="mt-1"
                      style={{ color: "var(--warning-500)" }}
                    >
                      {t(
                        "settings:customersConfiguration.addCustomerDialog.warnNameExists"
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.orgNumber"
                    )}
                  </label>
                  <UFDoraTextInput
                    error={!!errors.organizationNumber}
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.orgNumber"
                    )}
                    register={register("organizationNumber")}
                  />
                  {errors.organizationNumber && (
                    <div className="mt-1" style={{ color: "var(--error-500)" }}>
                      {errors.organizationNumber.message}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.address"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.address"
                    )}
                    register={register("address")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.postcode"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.postcode"
                    )}
                    register={register("postcode")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.city"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.city"
                    )}
                    register={register("city")}
                  />
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.email"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.email"
                    )}
                    error={!!errors.email}
                    register={register("email")}
                  />
                  {errors.email && (
                    <div className="mt-1" style={{ color: "var(--error-500)" }}>
                      {errors.email.message}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block">
                    {t(
                      "settings:customersConfiguration.addCustomerDialog.mobilePhone"
                    )}
                  </label>
                  <UFDoraTextInput
                    placeholder={t(
                      "settings:customersConfiguration.addCustomerDialog.mobilePhone"
                    )}
                    register={register("phoneNumberMobile")}
                  />
                </div>
              </div>

              {!id && (
                <div className="flex items-center gap-2 mt-4">
                  <DoraCheckbox register={register("visibleToDrivers")} />
                  <label className="text-sm">
                    {t(
                      "settings:customersConfiguration.customerPage.visibleToDriversCheckBox"
                    )}
                  </label>
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <div className="flex gap-2 justify-end items-center">
            <DoraButton type="button" variant={"primary"} onClick={onClose}>
              {t("components:buttonLabels.cancel")}
            </DoraButton>
            <DoraButton
              type="submit"
              variant={"primary-filled"}
              disabled={requestInProgress || !isValid}
            >
              {t("components:buttonLabels.save")}
            </DoraButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddEditBusinessCentralCustomerDialog;
